import { useCallback, useState } from 'react'

const SALES_API_URL = import.meta.env.VITE_SALES_API_URL as string

export type ContactFormProps = {
  idPrefix?: string
  className?: string
}

type Status = 'idle' | 'submitting' | 'success' | 'error'

export function ContactForm({ idPrefix = 'contact', className }: ContactFormProps) {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [message, setMessage] = useState('')
  const [honeypot, setHoneypot] = useState('')
  const [status, setStatus] = useState<Status>('idle')

  const id = (suffix: string) => `${idPrefix}-${suffix}`

  const onSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      setStatus('submitting')
      try {
        const res = await fetch(`${SALES_API_URL}/v1/leads`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name, email, phone, message, website: honeypot }),
        })
        if (!res.ok) throw new Error(`${res.status}`)
        setStatus('success')
      } catch {
        setStatus('error')
      }
    },
    [name, email, phone, message, honeypot],
  )

  if (status === 'success') {
    return (
      <div className="text-center py-4">
        <p className="fs-5 mb-1">Thanks — we'll be in touch soon.</p>
      </div>
    )
  }

  return (
    <form className={className ?? 'mx-auto'} style={{ maxWidth: 520 }} onSubmit={onSubmit}>
      {/* honeypot — hidden from real users, bots fill it */}
      <input
        tabIndex={-1}
        aria-hidden="true"
        style={{ display: 'none' }}
        id={id('website')}
        name="website"
        value={honeypot}
        onChange={(e) => setHoneypot(e.target.value)}
        autoComplete="off"
      />

      <div className="mb-3">
        <label htmlFor={id('name')} className="form-label">Name</label>
        <input
          id={id('name')}
          className="form-control"
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoComplete="name"
          disabled={status === 'submitting'}
        />
      </div>

      <div className="mb-3">
        <label htmlFor={id('email')} className="form-label">
          Email <span className="text-danger">*</span>
        </label>
        <input
          id={id('email')}
          type="email"
          className="form-control"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
          required
          disabled={status === 'submitting'}
        />
      </div>

      <div className="mb-3">
        <label htmlFor={id('phone')} className="form-label">Phone</label>
        <input
          id={id('phone')}
          type="tel"
          className="form-control"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          autoComplete="tel"
          disabled={status === 'submitting'}
        />
      </div>

      <div className="mb-4">
        <label htmlFor={id('message')} className="form-label">Message</label>
        <textarea
          id={id('message')}
          className="form-control"
          rows={5}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          disabled={status === 'submitting'}
        />
      </div>

      {status === 'error' && (
        <p className="text-danger small mb-3">Something went wrong — please try again.</p>
      )}

      <div className="d-grid">
        <button type="submit" className="btn btn-primary btn-lg" disabled={status === 'submitting'}>
          {status === 'submitting' ? 'Sending…' : 'Submit'}
        </button>
      </div>
    </form>
  )
}
